import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import AppLayout from 'layouts/AppLayout';
import Icon from 'components/AppIcon';
import AssetSelector from './AssetSelector';
import { ASSETS, OVERSIGHT_QUEUE } from '../data/assetData';
import { SUB_TAB_GROUPS } from 'config/navConfig';

// Asset Management sub-tabs sourced from central config
const TABS = SUB_TAB_GROUPS.assetManagement.map((tab) => ({
  label: tab.label,
  path: tab.path,
  icon: (() => {
    const iconMap = {
      'Operations Console': 'LayoutDashboard',
      'Oversight Hub':      'Eye',
      'Review Packs':       'FileText',
      'Report Builder':     'FilePlus',
      'Watchlist':          'AlertCircle',
      'Value Creation':     'TrendingUp',
      'Risk Monitor':       'ShieldAlert',
    };
    return iconMap[tab.label] || 'LayoutDashboard';
  })(),
}));

export default function AMPageShell({ children, activeTab, userRole = 'investor', onRoleChange }) {
  const [selectedIds, setSelectedIds] = useState(ASSETS?.map((a) => a?.id));
  const [userRoleState, setUserRoleState] = useState(userRole);
  const [toast, setToast] = useState(null);
  const [oversightQueue, setOversightQueue] = useState(OVERSIGHT_QUEUE);
  const navigate = useNavigate();

  const showToast = (message, type = 'success') => {
    setToast({ message, type });
    setTimeout(() => setToast(null), 3500);
  };

  const selectedAssets = selectedIds?.length === 0 || selectedIds?.length === ASSETS?.length
    ? ASSETS
    : ASSETS?.filter((a) => selectedIds?.includes(a?.id));

  return (
    <AppLayout userRole={userRoleState} onRoleChange={setUserRoleState}>
      {/* Toast */}
      {toast && (
        <div
          className="fixed top-5 right-5 flex items-center gap-3 px-4 py-3 rounded-lg shadow-lg border border-border bg-card z-50 max-w-sm"
          style={{ zIndex: 'var(--z-toast)' }}
        >
          <Icon
            name={toast?.type === 'success' ? 'CheckCircle' : 'AlertCircle'}
            size={18}
            color={toast?.type === 'success' ? 'var(--color-success)' : 'var(--color-warning)'}
          />
          <span className="font-caption text-foreground text-sm">{toast?.message}</span>
          <button onClick={() => setToast(null)} className="ml-auto">
            <Icon name="X" size={14} color="var(--color-muted-foreground)" />
          </button>
        </div>
      )}
      <div className="min-h-screen bg-background">
        {/* Page Header */}
        <div
          className="sticky top-0 bg-background border-b border-border px-6 md:px-10 lg:px-12 py-4 flex items-center justify-between gap-4 flex-wrap"
          style={{ zIndex: 'var(--z-nav)' }}
        >
          <div className="pl-10 lg:pl-0 flex items-center gap-3">
            <div className="flex items-center justify-center w-8 h-8 rounded-md" style={{ background: 'var(--color-primary)' }}>
              <Icon name="Building2" size={16} color="white" strokeWidth={2} />
            </div>
            <div>
              <h1 className="font-heading font-semibold text-foreground" style={{ fontSize: '1.375rem', lineHeight: 1.3 }}>
                Asset Management
              </h1>
              <p className="font-caption text-muted-foreground" style={{ fontSize: '0.75rem' }}>Sample data · Demo prototype</p>
            </div>
          </div>
          <div className="flex items-center gap-3">
            <AssetSelector selectedIds={selectedIds} onChange={setSelectedIds} />
          </div>
        </div>

        {/* Sub-tab navigation */}
        <div className="bg-card border-b border-border px-6 md:px-10 lg:px-12 overflow-x-auto">
          <nav className="flex gap-0 min-w-max">
            {TABS?.map((tab) => {
              const active = activeTab === tab?.path;
              return (
                <button
                  key={tab?.path}
                  onClick={() => navigate(tab?.path)}
                  className={`flex items-center gap-1.5 px-4 py-3 text-sm font-caption font-medium border-b-2 transition-all duration-250 whitespace-nowrap ${
                    active
                      ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground hover:border-border'
                  }`}
                >
                  <Icon name={tab?.icon} size={14} color={active ? 'var(--color-primary)' : 'currentColor'} strokeWidth={active ? 2.5 : 2} />
                  {tab?.label}
                </button>
              );
            })}
          </nav>
        </div>

        {/* Page content — pass selectedAssets and showToast via React.cloneElement */}
        <div className="px-6 md:px-10 lg:px-12 py-8">
          {React.Children?.map(children, (child) =>
            React.isValidElement(child)
              ? React.cloneElement(child, { selectedAssets, showToast, oversightQueue, setOversightQueue })
              : child
          )}
        </div>
      </div>
    </AppLayout>
  );
}
